import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '../components/Secciones/Header';
import Footer from '../components/Secciones/Footer';
import BrandLogoImage from '../components/BrandLogoImage';
import BrandSeo from '../components/BrandSeo';
import { getProductImageUrl, useProductImageFallback } from '../utils/productImages';
import './BrandResults.css';

const API_BASE = 'https://valleyapi-dr9y.onrender.com';

export default function BrandResults() {
  const { idbrand } = useParams();
  const [brand, setBrand] = useState(null);
  const [products, setProducts] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();

    async function loadBrandCatalog() {
      setLoading(true);
      setError('');
      try {
        const brandResponse = await fetch(`${API_BASE}/api/brands/${encodeURIComponent(idbrand)}`, {
          signal: controller.signal,
          cache: 'no-store'
        });
        if (!brandResponse.ok) throw new Error('Brand not found');
        const brandData = await brandResponse.json();
        if (!brandData?.brand_name) throw new Error('Brand information is incomplete');

        const productsUrl = new URL(`${API_BASE}/api/products/search`);
        productsUrl.searchParams.set('q', '*');
        productsUrl.searchParams.set('brand', brandData.brand_name);
        productsUrl.searchParams.set('page', '1');
        productsUrl.searchParams.set('limit', '48');
        productsUrl.searchParams.set('sort', 'rank_asc');

        const productsResponse = await fetch(productsUrl, {
          signal: controller.signal,
          cache: 'no-store'
        });
        if (!productsResponse.ok) throw new Error('Products unavailable');
        const productData = await productsResponse.json();
        const productList = Array.isArray(productData)
          ? productData
          : (Array.isArray(productData?.products) ? productData.products : []);

        setBrand(brandData);
        setProducts(productList.filter((product) => product?.published !== 'No'));
        setTotalProducts(Number(productData?.totalResults) || productList.length);
      } catch (requestError) {
        if (requestError.name !== 'AbortError') {
          console.error('Unable to load brand catalog:', requestError);
          setError('We could not load this manufacturer catalog.');
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    loadBrandCatalog();
    return () => controller.abort();
  }, [idbrand]);

  const fullCatalogUrl = brand?.brand_name
    ? `/search?q=*&brand=${encodeURIComponent(brand.brand_name)}&page=1`
    : '/search?q=*';

  return (
    <div className="brand-results-page">
      {brand && <BrandSeo brand={brand} products={products} />}
      <Header />
      <main>
        {loading ? (
          <div className="brand-results-state" aria-label="Loading manufacturer catalog">
            <div className="brand-results-spinner" />
            <p>Loading manufacturer products...</p>
          </div>
        ) : error ? (
          <div className="brand-results-state">
            <h1>Manufacturer unavailable</h1>
            <p>{error}</p>
            <Link to="/brands">Browse all manufacturers</Link>
          </div>
        ) : (
          <>
            <section className="brand-results-hero">
              <div className="brand-results-container">
                <nav className="brand-results-breadcrumbs" aria-label="Breadcrumb">
                  <Link to="/">Home</Link><span aria-hidden="true">/</span>
                  <Link to="/brands">Brands</Link><span aria-hidden="true">/</span>
                  <span aria-current="page">{brand.brand_name}</span>
                </nav>
                <div className="brand-results-heading">
                  <div className="brand-results-logo">
                    <span>{String(brand.brand_name).slice(0, 2).toUpperCase()}</span>
                    <BrandLogoImage brand={brand} loading="eager" />
                  </div>
                  <div>
                    <span className="brand-results-eyebrow">Industrial manufacturer</span>
                    <h1>{brand.brand_name} industrial products</h1>
                    <p>{brand.brand_description || `Explore ${brand.brand_name} products for industrial automation, controls and production applications.`}</p>
                    <div className="brand-results-summary">
                      <strong>{totalProducts}</strong>
                      <span>{totalProducts === 1 ? 'product found' : 'products found'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="brand-results-products" aria-labelledby="brand-products-title">
              <div className="brand-results-container">
                <div className="brand-results-section-heading">
                  <div>
                    <span className="brand-results-eyebrow">Manufacturer catalog</span>
                    <h2 id="brand-products-title">Browse {brand.brand_name} products</h2>
                  </div>
                  <Link to={fullCatalogUrl}>Search and filter this brand <span aria-hidden="true">→</span></Link>
                </div>

                {products.length ? (
                  <div className="brand-results-grid">
                    {products.map((product) => (
                      <article className="brand-results-card" key={product.idproducts || product.product_name}>
                        <Link className="brand-results-image" to={`/products/${encodeURIComponent(product.product_name)}`}>
                          <img
                            src={getProductImageUrl(product.product_image)}
                            alt={product.product_name}
                            loading="lazy"
                            onError={useProductImageFallback}
                          />
                        </Link>
                        <div className="brand-results-card-content">
                          <span>{product.product_category || 'Industrial product'}</span>
                          <h3><Link to={`/products/${encodeURIComponent(product.product_name)}`}>{product.product_name}</Link></h3>
                          <p>{product.product_description || `${brand.brand_name} component for industrial applications.`}</p>
                          <Link className="brand-results-product-link" to={`/products/${encodeURIComponent(product.product_name)}`}>
                            View product details <span aria-hidden="true">→</span>
                          </Link>
                        </div>
                      </article>
                    ))}
                  </div>
                ) : (
                  <div className="brand-results-empty">
                    <h2>No published products are available for this brand yet.</h2>
                    <p>Contact our team for sourcing and product availability.</p>
                    <Link to="/about#contact">Request product assistance</Link>
                  </div>
                )}

                {totalProducts > products.length && (
                  <div className="brand-results-more">
                    <p>Showing {products.length} of {totalProducts} products.</p>
                    <Link to={fullCatalogUrl}>View the complete searchable catalog</Link>
                  </div>
                )}
              </div>
            </section>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
